/**
 * TableEditorSidebar — Designer-side composite sidebar that hosts the
 * always-on dependency-annotation pip (Slice H2) plus the opt-in mini-graph
 * toggle + panel (Slice H3) plus the "Open in Dependency Viewer" hand-off.
 *
 * Extracted from `TableEditor.tsx` to keep that file under the 200-line
 * CLAUDE.md budget.
 */
import type {
  DependencyAnnotations,
  DependencyItem,
} from '../../dependencies/types';
import { openInDependencyViewer } from '../../dependents/openInDependencyViewer';
import { miniGraphObjectKey, useVqeDdlStore } from '../../store';
import { DependencySidebar } from '../common/DependencySidebar';
import { MiniGraphPanel } from '../common/MiniGraphPanel';
import { MiniGraphToggleButton } from '../common/MiniGraphToggleButton';

export interface TableEditorSidebarProps {
  readonly annotations: DependencyAnnotations;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly mode: 'connected' | 'disconnected';
  readonly schema: string | null;
  readonly objectName: string | null;
  readonly kindLabel: string;
  readonly connectionId: string | null | undefined;
  readonly database: string | null | undefined;
  readonly showLifecycleActions: boolean;
  readonly testId: string;
}

export function TableEditorSidebar({
  annotations,
  isLoading,
  error,
  mode,
  schema,
  objectName,
  kindLabel,
  connectionId,
  database,
  showLifecycleActions,
  testId,
}: TableEditorSidebarProps) {
  const miniGraphKey = miniGraphObjectKey({
    connectionId,
    database,
    schema,
    objectName,
  });
  const miniGraphOpen = useVqeDdlStore(
    (s) => s.miniGraphOpenByObject[miniGraphKey] ?? false,
  );

  const handleOpenInDependencyViewer = () => {
    openInDependencyViewer({
      connectionId,
      database,
      schema,
      objectName,
      objectType: 'table',
    });
  };

  const handleJumpToNeighbor = (item: DependencyItem) => {
    openInDependencyViewer({
      connectionId,
      database,
      schema: item.schema,
      objectName: item.name,
      objectType: item.objectType,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <DependencySidebar
        annotations={annotations}
        isLoading={isLoading}
        error={error}
        mode={mode}
        testId={`${testId}-dependencies`}
      />
      {showLifecycleActions ? (
        <MiniGraphToggleButton
          connectionId={connectionId}
          database={database}
          schema={schema}
          objectName={objectName}
          testId={`${testId}-mini-graph-toggle`}
        />
      ) : null}
      {miniGraphOpen && showLifecycleActions ? (
        <MiniGraphPanel
          annotations={annotations}
          center={{ schema, objectName, kindLabel }}
          onOpenInDependencyViewer={handleOpenInDependencyViewer}
          onJumpToNeighbor={handleJumpToNeighbor}
          testId={`${testId}-mini-graph`}
        />
      ) : null}
    </div>
  );
}
